import React, {Component} from 'react';
import {Route, Switch, Redirect} from 'react-router-dom';
import {Provider, connect} from 'react-redux';
import axios from 'utils/request';
import firebase from 'utils/firebase';
import {setFavoritePicks} from 'actions/favoritePicks';
import {setFollowingPicks} from 'actions/followingPicks';
import {setFollowingStrategies} from 'actions/followingStrategies';
import {setUserPayments} from 'actions/user';
import {Row, Col} from 'components/Grid';
import {
  getFollowingStrategiesRequest,
  getFavoritePicksRequest,
  getUserPaymentsRequest,
  getPicksRequest,
} from 'requests';
import {Loader} from 'components/Loader';
import Aligner from 'components/Aligner';
import store from './store';

import AppBar from './layout/AppBar';
import Pick from './apps/Pick';
import Page404 from './apps/Page404';
import Picks from './apps/Picks';
import TopStrategies from './apps/Strategies/Top';
import FollowingStrategies from './apps/Strategies/Following';
import SettledFavorites from './apps/Stats/SettledFavorites';
import Strategy from './apps/Strategy';
import LastPicks from './apps/Strategy/pages/LastPicks';

import AccountPage from './apps/Account';
import PaymentHistory from './apps/Account/pages/PaymentHistory';
import Payment from './apps/Payment';
import 'normalize.css';
import './app.scss';

class App extends Component {
  state = {
    loading: true,
  };

  async componentDidMount() {
    try {
      const [followingPicks, followingStrategies, favoritePicks, userPayments] = await Promise.all([
        getPicksRequest(),
        getFollowingStrategiesRequest(),
        getFavoritePicksRequest(),
        getUserPaymentsRequest(),
      ]);

      this.props.setFavoritePicks(favoritePicks);
      this.props.setFollowingPicks(followingPicks);
      this.props.setFollowingStrategies(followingStrategies);
      this.props.setUserPayments(userPayments);
    } catch (e) {
      console.error(e);
    }
    try {
      const userToken = await firebase.auth().currentUser.getIdToken();
      const userEmail = firebase.auth().currentUser.email;
      const followingStrategies = await axios.get('/v3/strategies', {
        headers: {token: userToken},
      });
      const favoritePicks = await axios.get('/v3/favorites', {
        headers: {token: userToken},
      });
      this.setState({
        followingStrategies: followingStrategies.data,
        favoritePicks: favoritePicks.data,
        loading: false,
        userEmail,
      });
    } catch (error) {
      console.error(error);
    }
  }

  onFollowStrategy = async strategy => {
    try {
      const userToken = await firebase.auth().currentUser.getIdToken();
      const followedStrategy = await axios({
        method: 'post',
        url: `/v3/strategies/${strategy.id}/follow`,
        headers: {
          token: userToken,
        },
      });
      this.setState(prevState => ({
        followingStrategies: [
          ...prevState.followingStrategies,
          {...followedStrategy.data, subsAmount: strategy.subsAmount + 1},
        ],
      }));
    } catch (error) {
      console.error(error);
    }
  };

  onUnfollowStrategy = async strategy => {
    try {
      if (window.confirm(`Are you sure you want to unfollow the strategy and all associated picks?`)) {
        const userToken = await firebase.auth().currentUser.getIdToken();
        await axios({
          method: 'delete',
          url: `/v3/strategies/${strategy.id}`,
          headers: {
            token: userToken,
          },
        });
        this.setState(prevState => ({
          followingStrategies: prevState.followingStrategies.filter(strategyObj => strategyObj.id !== strategy.id),
        }));
      }
    } catch (error) {
      console.error(error);
    }
  };

  onAddPickToFavorites = async pick => {
    try {
      const userToken = await firebase.auth().currentUser.getIdToken();
      const addedFavorite = await axios({
        method: 'post',
        url: `/v3/favorites`,
        headers: {
          token: userToken,
        },
        data: {
          pick: pick.pick,
          eventId: pick.event.id,
        },
      });
      this.setState(prevState => ({
        favoritePicks: [...prevState.favoritePicks, addedFavorite.data],
      }));
    } catch (error) {
      console.error(error);
    }
  };

  onDeletePickFromFavorites = async pick => {
    try {
      if (window.confirm(`Are you sure you remove this pick from your Favorites Picks?`)) {
        const userToken = await firebase.auth().currentUser.getIdToken();
        await axios({
          method: 'delete',
          url: `/v3/favorites/${pick.id}`,
          headers: {
            token: userToken,
          },
        });
        this.setState(prevState => ({
          favoritePicks: prevState.favoritePicks.filter(prevPick => pick.id !== prevPick.id),
        }));
      }
    } catch (error) {
      console.error(error);
    }
  };

  onLogout = async () => {
    try {
      await firebase.auth().signOut();
    } catch (error) {
      console.error(error);
    }
  };

  render() {
    const {followingStrategies, loading, userEmail} = this.state;
    if (loading) {
      return <Loader />;
    }
    return (
      <div className="app">
        <AppBar />
        <Aligner className="content">
          <Row>
            <Col size={8}>
              <div className="page">
                <Switch>
                  <Route exact path="/" render={() => <Redirect to="/strategies" />} />
                  <Route
                    exact
                    path="/strategies"
                    render={() => (
                      <React.Fragment>
                        <TopStrategies
                          followingStrategies={followingStrategies}
                          onFollowStrategy={this.onFollowStrategy}
                          onUnfollowStrategy={this.onUnfollowStrategy}
                        />
                      </React.Fragment>
                    )}
                  />
                  <Route exact path="/strategies/following" render={() => <FollowingStrategies />} />
                  <Route exact path="/s/:strategyId" component={Strategy} />
                  <Route exact path="/s/:strategyId/last-picks" component={LastPicks} />
                  <Route
                    exact
                    path="/account"
                    render={() => (
                      <React.Fragment>
                        <AccountPage userEmail={userEmail} />
                      </React.Fragment>
                    )}
                  />
                  <Route exact path="/account/payments" component={PaymentHistory} />
                  <Route
                    exact
                    path="/account/payments/:paymentId"
                    render={props => (
                      <React.Fragment>
                        <Payment props={props} />
                      </React.Fragment>
                    )}
                  />
                  <Route
                    path="/stats"
                    render={() => (
                      <React.Fragment>
                        <SettledFavorites />
                      </React.Fragment>
                    )}
                  />
                  <Route path="/s/:strategyId/p/:pickId" component={Pick} />
                  <Route path="*" component={Page404} />
                </Switch>
              </div>
            </Col>
            <Col size={4} className="right-side">
              <Picks />
            </Col>
          </Row>
        </Aligner>
      </div>
    );
  }
}

const ConnectedApp = connect(null, dispatch => ({
  setFavoritePicks: picks => dispatch(setFavoritePicks(picks)),
  setFollowingPicks: picks => dispatch(setFollowingPicks(picks)),
  setFollowingStrategies: strategies => dispatch(setFollowingStrategies(strategies)),
  setUserPayments: payments => dispatch(setUserPayments(payments)),
}))(App);

export default () => (
  <Provider store={store}>
    <ConnectedApp />
  </Provider>
);
